import { memo, useMemo, type FC, type SyntheticEvent } from 'react';
import { useMediaQuery } from 'react-responsive';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { getDataTestId } from '@/js/libs/utils/get-data-test-id/index.ts';

import { Media } from '../../../../../theme/Media.tsx';
import { Select } from '../../../Select/index.tsx';
import { isDefinedDateRange } from '../../helpers.ts';
import type { DateRangeType, DateType, ValidDateRange, Option, RangePreset, DatePickerRangeTranslations } from '../../types.ts';
import { PresetItem } from './PresetItem.tsx';
import { Header, MobileHeader, PresetsWrapper } from './styled.ts';

interface RangePresetsProps {
  presets: RangePreset[];
  startDate: DateType;
  endDate: DateType;
  onChange: (dateRange: ValidDateRange, e?: SyntheticEvent<HTMLDivElement, MouseEvent>) => void;
  translations: Required<DatePickerRangeTranslations>;
}

export const RangePresets: FC<RangePresetsProps> = memo(function RangePresets({ presets, startDate, endDate, onChange, translations }) {
  const title = translations.period;
  const isMobile = useMediaQuery({ query: Media.xsMax });

  const selectOptions = useMemo<Option<DateRangeType>[]>(
    () =>
      presets.map(({ title, dateRange }) => ({
        label: title,
        value: dateRange,
      })),
    [presets],
  );

  const selectedValue = useMemo<Option<DateRangeType> | undefined>(() => {
    const activePreset = presets.find(({ isActive, dateRange }) => isActive([startDate, endDate], dateRange));
    return activePreset ? { label: activePreset.title, value: activePreset.dateRange } : undefined;
  }, [endDate, presets, startDate]);

  const handleSelectChange = useCallbackRef((option: Option<DateRangeType> | null) => {
    if (option?.value && isDefinedDateRange(option.value)) {
      onChange(option.value);
    }
  });

  const createRangePresetClickHandler = useCallbackRef((dateRange: DateRangeType) => () => {
    if (isDefinedDateRange(dateRange)) {
      onChange(dateRange);
    }
  });

  return (
    <div>
      {isMobile ? (
        <div>
          <MobileHeader>{title}</MobileHeader>
          <Select options={selectOptions} value={selectedValue} onChange={handleSelectChange} />
        </div>
      ) : (
        <>
          <Header>{title}</Header>
          <PresetsWrapper>
            {presets.map(({ isActive, title, dateRange, dataTestId }) => {
              const isActivePreset = isActive([startDate, endDate], dateRange);
              return (
                <PresetItem
                  key={title}
                  title={title}
                  isActive={isActivePreset}
                  onClick={createRangePresetClickHandler(dateRange)}
                  dataTestId={getDataTestId(dataTestId)}
                  isCustom={!dateRange[0] && !dateRange[1]}
                  range
                />
              );
            })}
          </PresetsWrapper>
        </>
      )}
    </div>
  );
});
